import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { useToast } from '@/components/ui/Toast';
import {
  useFieldLibrary,
  useCreateFieldLibraryItem,
  useUpdateFieldLibraryItem,
  useDeleteFieldLibraryItem,
  type FieldLibraryItem,
  type FieldType,
  type FieldWidth,
} from './hooks/useFormBuilder';
import { LOOKUP_CATEGORIES, CATEGORY_LABELS } from '@/features/settings/hooks/useSystemLookups';
import { useRoles } from '@/features/roles/hooks/useRoles';

const FIELD_TYPE_OPTIONS: Array<{ value: FieldType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
  { value: 'file', label: 'File Upload' },
  { value: 'table', label: 'Table (Repeatable Rows)' },
  { value: 'country', label: 'Country (dropdown)' },
  { value: 'postal_code', label: 'Postal Code (verified)' },
  { value: 'user_select', label: 'System User (by role)' },
];

const WIDTH_OPTIONS: Array<{ value: FieldWidth; label: string }> = [
  { value: 'full', label: 'Full Width' },
  { value: 'half', label: 'Half Width' },
  { value: 'third', label: 'One Third' },
];

/**
 * FieldLibraryPage — manage reusable field definitions.
 * Fields defined here can be attached to any form via the form builder.
 */
export function FieldLibraryPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: fields, isLoading } = useFieldLibrary();
  const deleteMutation = useDeleteFieldLibraryItem();
  const [editingField, setEditingField] = useState<FieldLibraryItem | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const handleDelete = (field: FieldLibraryItem) => {
    if (confirm(`Deactivate "${field.label}"? It won't be available for new forms.`)) {
      deleteMutation.mutate(field.id, {
        onSuccess: () => toast({ variant: 'success', title: 'Field deactivated' }),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  const systemFields = (fields ?? []).filter((f) => f.is_system_field);
  const customFields = (fields ?? []).filter((f) => !f.is_system_field);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/settings/form-builder')}
            className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 mb-1"
          >
            &larr; Back to Form Builder
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Field Library</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Define reusable fields here. When building a form, you pick fields from this library.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingField(null);
            setIsEditorOpen(true);
          }}
        >
          New Custom Field
        </Button>
      </div>

      {/* System Fields (read-only list) */}
      <Card>
        <CardHeader
          title="System Fields"
          description="Built-in fields mapped to database columns. Properties can be edited but field_key and db_column are fixed."
        />
        <CardContent>
          <div className="divide-y divide-border dark:divide-border-dark">
            {systemFields.map((field) => (
              <div key={field.id} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {field.label}
                  </span>
                  <span className="text-xs text-gray-500">({field.field_key})</span>
                  <Badge variant="default" size="sm">
                    {field.field_type}
                  </Badge>
                  <Badge variant="info" size="sm">
                    System
                  </Badge>
                  {field.lookup_category && (
                    <Badge variant="default" size="sm">
                      Lookup: {field.lookup_category}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingField(field);
                    setIsEditorOpen(true);
                  }}
                >
                  Edit
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Fields */}
      <Card>
        <CardHeader
          title="Custom Fields"
          description="User-defined fields. Values stored in the form field values table."
        />
        <CardContent>
          {customFields.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">
              No custom fields defined yet. Click "New Custom Field" to create one.
            </p>
          ) : (
            <div className="divide-y divide-border dark:divide-border-dark">
              {customFields.map((field) => (
                <div key={field.id} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {field.label}
                    </span>
                    <span className="text-xs text-gray-500">({field.field_key})</span>
                    <Badge variant="default" size="sm">
                      {field.field_type}
                    </Badge>
                    {field.lookup_category && (
                      <Badge variant="default" size="sm">
                        Lookup: {field.lookup_category}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingField(field);
                        setIsEditorOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(field)}
                      className="text-red-600"
                    >
                      Deactivate
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isEditorOpen && (
        <FieldLibraryEditorModal
          field={editingField}
          allFields={fields ?? []}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingField(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Field Library Editor Modal ──────────────────────────────────────────────

/**
 * A titled, bordered group of related fields within the editor modal. Keeps the
 * form readable by splitting it into labelled sections instead of one long
 * scroll. Always visible (not collapsible) so nothing important is hidden.
 */
function FieldFormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border p-4 dark:border-border-dark">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {description && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function FieldLibraryEditorModal({
  field,
  allFields,
  onClose,
}: {
  field: FieldLibraryItem | null;
  allFields: FieldLibraryItem[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const createMutation = useCreateFieldLibraryItem();
  const updateMutation = useUpdateFieldLibraryItem();
  const { data: roles } = useRoles();
  const isEditing = !!field;
  const editingField = field;
  const fields = allFields;

  const [form, setForm] = useState({
    field_key: field?.field_key ?? '',
    label: field?.label ?? '',
    description: field?.description ?? '',
    field_type: field?.field_type ?? ('text' as FieldType),
    is_system_field: field?.is_system_field ?? false,
    db_column: field?.db_column ?? '',
    lookup_category: field?.lookup_category ?? '',
    placeholder: field?.placeholder ?? '',
    hint_text: field?.hint_text ?? '',
    default_value: field?.default_value ?? '',
    width: field?.width ?? ('half' as FieldWidth),
    options: field?.options ?? ([] as Array<{ value: string; label: string }>),
    validation_rules: (field?.validation_rules ?? {}) as Record<string, unknown>,
  });

  const [newOptValue, setNewOptValue] = useState('');
  const [newOptLabel, setNewOptLabel] = useState('');
  // Sample value for the live regex tester shown under the Format input.
  const [patternSample, setPatternSample] = useState('');

  const addOption = () => {
    if (!newOptValue.trim()) return;
    setForm((p) => ({
      ...p,
      options: [
        ...p.options,
        { value: newOptValue.trim(), label: newOptLabel.trim() || newOptValue.trim() },
      ],
    }));
    setNewOptValue('');
    setNewOptLabel('');
  };

  const removeOption = (idx: number) =>
    setForm((p) => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));

  // Validate the entered regex so a broken pattern can't be saved (which would
  // silently skip format enforcement at render time).
  const patternValue = (form.validation_rules.pattern as string) ?? '';
  let regexError: string | null = null;
  if (patternValue.trim()) {
    try {
      new RegExp(patternValue);
    } catch (err) {
      regexError = `Invalid regex: ${(err as Error).message}`;
    }
  }

  // Live result of testing the sample value against the entered pattern.
  // null = nothing to show (no pattern, invalid pattern, or empty sample).
  let patternSampleMatches: boolean | null = null;
  if (patternValue.trim() && !regexError && patternSample !== '') {
    try {
      patternSampleMatches = new RegExp(patternValue).test(patternSample);
    } catch {
      patternSampleMatches = null;
    }
  }

  const handleSave = () => {
    if (regexError) {
      toast({ variant: 'error', title: 'Invalid format', description: regexError });
      return;
    }

    // Normalize validation_rules: drop empty pattern/message so we don't persist
    // blank keys.
    const vrules: Record<string, unknown> = { ...form.validation_rules };
    if (!patternValue.trim()) {
      delete vrules.pattern;
      delete vrules.pattern_message;
    }

    const payload: Record<string, unknown> = {
      ...form,
      validation_rules: vrules,
      db_column: form.db_column || null,
      lookup_category: form.lookup_category || null,
      placeholder: form.placeholder || null,
      hint_text: form.hint_text || null,
      default_value: form.default_value || null,
      description: form.description || null,
    };

    if (isEditing && field) {
      updateMutation.mutate(
        { id: field.id, data: payload },
        {
          onSuccess: () => {
            toast({ variant: 'success', title: 'Field updated' });
            onClose();
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Field created' });
          onClose();
        },
      });
    }
  };

  const showOptions = ['select', 'multi_select', 'radio', 'table'].includes(form.field_type);
  const isTextLike = ['text', 'textarea', 'email', 'phone'].includes(form.field_type);
  const hasOptionsSection =
    form.field_type === 'user_select' ||
    ['select', 'multi_select'].includes(form.field_type) ||
    showOptions;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? `Edit: ${field!.label}` : 'New Custom Field'}
      size="lg"
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
        {/* ── Basics ─────────────────────────────────────────────────── */}
        <FieldFormSection
          title="Basics"
          description="Identify this field and describe what it's for."
        >
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Field Key"
              value={form.field_key}
              onChange={(e) => setForm((p) => ({ ...p, field_key: e.target.value }))}
              required
              placeholder="e.g., tax_id_number"
              hint="Unique snake_case identifier"
              disabled={isEditing}
            />
            <Input
              label="Label"
              value={form.label}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              required
              placeholder="e.g., Tax ID Number"
            />
          </div>
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="What is this field for?"
          />
        </FieldFormSection>

        {/* ── Appearance ─────────────────────────────────────────────── */}
        <FieldFormSection
          title="Type & appearance"
          description="How the field behaves and looks on the form."
        >
          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Field Type"
              value={form.field_type}
              onChange={(e) => setForm((p) => ({ ...p, field_type: e.target.value as FieldType }))}
              options={FIELD_TYPE_OPTIONS}
            />
            <Select
              label="Width"
              value={form.width}
              onChange={(e) => setForm((p) => ({ ...p, width: e.target.value as FieldWidth }))}
              options={WIDTH_OPTIONS}
            />
            <Input
              label="Placeholder"
              value={form.placeholder}
              onChange={(e) => setForm((p) => ({ ...p, placeholder: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Hint Text"
              value={form.hint_text}
              onChange={(e) => setForm((p) => ({ ...p, hint_text: e.target.value }))}
            />
            <Input
              label="Default Value"
              value={form.default_value}
              onChange={(e) => setForm((p) => ({ ...p, default_value: e.target.value }))}
            />
          </div>
        </FieldFormSection>

        {/* ── Validation / Format (text-like types only) ─────────────── */}
        {isTextLike && (
          <FieldFormSection
            title="Format & validation"
            description="Optionally require input to match a pattern, and test it live."
          >
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Format (regex)"
                value={(form.validation_rules.pattern as string) ?? ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    validation_rules: { ...p.validation_rules, pattern: e.target.value },
                  }))
                }
                placeholder="e.g. ^[A-Z]{2}\\d{6}$"
                hint="Input must match this pattern. Leave blank for no format check."
                error={regexError ?? undefined}
              />
              <Input
                label="Format error message"
                value={(form.validation_rules.pattern_message as string) ?? ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    validation_rules: { ...p.validation_rules, pattern_message: e.target.value },
                  }))
                }
                placeholder="e.g. Enter a valid tax ID (AB123456)"
                hint="Shown when the input doesn't match."
              />

              {/* Live tester — type a sample value and see if it matches */}
              {patternValue.trim() && !regexError && (
                <div className="col-span-2">
                  <Input
                    label="Preview — test a sample value"
                    value={patternSample}
                    onChange={(e) => setPatternSample(e.target.value)}
                    placeholder="Type a value to test against the format"
                  />
                  {patternSample !== '' && patternSampleMatches !== null && (
                    <div
                      className={`mt-1.5 flex items-center gap-1.5 text-sm ${
                        patternSampleMatches
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-danger-500'
                      }`}
                      role="status"
                    >
                      {patternSampleMatches ? (
                        <>
                          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 111.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Matches the format
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.7 7.29a1 1 0 00-1.41 1.42L8.59 10l-1.3 1.29a1 1 0 101.41 1.42L10 11.41l1.29 1.3a1 1 0 001.42-1.42L11.41 10l1.3-1.29a1 1 0 00-1.42-1.42L10 8.59l-1.3-1.3z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {(form.validation_rules.pattern_message as string)?.trim() ||
                            "Doesn't match the format"}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </FieldFormSection>
        )}

        {/* ── Options / data source ──────────────────────────────────── */}
        {hasOptionsSection && (
          <FieldFormSection
            title="Options & data source"
            description={
              form.field_type === 'user_select'
                ? 'Choose which users this picker lists.'
                : form.field_type === 'table'
                  ? 'Define the columns for this table.'
                  : 'Where this dropdown gets its choices.'
            }
          >
            {/* System User field — role filter config */}
            {form.field_type === 'user_select' && (
              <div className="rounded-lg border border-border p-3 dark:border-border-dark">
                <MultiSelect
                  label="Roles to include"
                  options={(roles ?? []).map((r) => ({ value: r.id, label: r.name }))}
                  value={(form.validation_rules.roles as string[]) ?? []}
                  onChange={(vals) =>
                    setForm((p) => ({
                      ...p,
                      validation_rules: { ...p.validation_rules, roles: vals },
                    }))
                  }
                  placeholder="All roles (leave empty to include everyone)"
                  hint="The dropdown will list active users who have any of the selected roles. Leave empty to list all users."
                />
              </div>
            )}

            {/* Lookup Category */}
            {['select', 'multi_select'].includes(form.field_type) && (
              <Select
                label="Lookup Category (optional — loads options from System Lookups)"
                value={form.lookup_category}
                onChange={(e) => setForm((p) => ({ ...p, lookup_category: e.target.value }))}
                options={LOOKUP_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
                placeholder="None (use static options below)"
              />
            )}

            {/* Static Options / Table Columns */}
            {showOptions && !form.lookup_category && (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {form.field_type === 'table' ? 'Table Columns' : 'Options'}
                </label>

                {/* TABLE COLUMN BUILDER — polished UI */}
                {form.field_type === 'table' && (
                  <>
                    <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                      Define the columns for this table. Use fields from the library or add custom
                      columns manually.
                    </p>

                    {/* Column list */}
                    {form.options.length > 0 && (
                      <div className="mb-4 space-y-2">
                        <div className="rounded-lg border border-border dark:border-border-dark overflow-hidden">
                          {/* Table header */}
                          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center bg-gray-50 dark:bg-surface-dark-tertiary px-3 py-2 border-b border-border dark:border-border-dark">
                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Column
                            </span>
                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide text-center w-16">
                              Required
                            </span>
                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide text-center w-16">
                              Unique
                            </span>
                            <span className="w-8" />
                          </div>

                          {/* Column rows */}
                          {form.options.map((opt, idx) => {
                            const colRequired =
                              ((form.validation_rules?.column_required as Record<
                                string,
                                boolean
                              >) ?? {})[opt.value] ?? false;
                            const colUnique =
                              ((form.validation_rules?.column_unique as Record<string, boolean>) ??
                                {})[opt.value] ?? false;
                            const colType = (
                              (form.validation_rules as Record<string, unknown>)?.column_types as
                                | Record<string, string>
                                | undefined
                            )?.[opt.value];
                            const isFromLibrary = fields?.some((f) => f.field_key === opt.value);

                            const toggleRequired = () => {
                              const currentRules = { ...form.validation_rules };
                              const req = {
                                ...((currentRules.column_required as Record<string, boolean>) ||
                                  {}),
                              };
                              req[opt.value] = !colRequired;
                              setForm((p) => ({
                                ...p,
                                validation_rules: { ...currentRules, column_required: req },
                              }));
                            };

                            const toggleUnique = () => {
                              const currentRules = { ...form.validation_rules };
                              const uniq = {
                                ...((currentRules.column_unique as Record<string, boolean>) || {}),
                              };
                              uniq[opt.value] = !colUnique;
                              setForm((p) => ({
                                ...p,
                                validation_rules: { ...currentRules, column_unique: uniq },
                              }));
                            };

                            return (
                              <div
                                key={idx}
                                className={`grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-3 py-2.5 ${
                                  idx !== form.options.length - 1
                                    ? 'border-b border-border dark:border-border-dark'
                                    : ''
                                } hover:bg-gray-50/50 dark:hover:bg-surface-dark-secondary/50 transition-colors`}
                              >
                                {/* Column info */}
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-gray-400 dark:text-gray-500 text-xs cursor-grab">
                                    ⋮⋮
                                  </span>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {opt.label}
                                      </span>
                                      {colType && (
                                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                          {colType}
                                        </span>
                                      )}
                                      {isFromLibrary && (
                                        <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                          Library
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                      {opt.value}
                                    </span>
                                  </div>
                                </div>

                                {/* Required toggle */}
                                <div className="flex justify-center w-16">
                                  <button
                                    type="button"
                                    onClick={toggleRequired}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                      colRequired ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'
                                    }`}
                                    title={
                                      colRequired ? 'Column is required' : 'Column is optional'
                                    }
                                  >
                                    <span
                                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                        colRequired ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                      }`}
                                    />
                                  </button>
                                </div>

                                {/* Unique toggle */}
                                <div className="flex justify-center w-16">
                                  <button
                                    type="button"
                                    onClick={toggleUnique}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                      colUnique ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'
                                    }`}
                                    title={
                                      colUnique ? 'Values must be unique' : 'Duplicates allowed'
                                    }
                                  >
                                    <span
                                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                        colUnique ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                      }`}
                                    />
                                  </button>
                                </div>

                                {/* Remove */}
                                <div className="flex justify-center w-8">
                                  <button
                                    type="button"
                                    onClick={() => removeOption(idx)}
                                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 transition-colors"
                                    title="Remove column"
                                  >
                                    <svg
                                      className="h-4 w-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">
                          {form.options.length} column{form.options.length !== 1 ? 's' : ''} defined
                        </p>
                      </div>
                    )}

                    {/* Add column section */}
                    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3 space-y-3">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        Add Column
                      </span>

                      {/* From field library */}
                      {fields && fields.length > 0 && (
                        <div>
                          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                            From Field Library
                          </label>
                          <select
                            className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                            onChange={(e) => {
                              const selected = fields.find((f) => f.id === e.target.value);
                              if (
                                selected &&
                                !form.options.some((o) => o.value === selected.field_key)
                              ) {
                                const currentRules = (form.validation_rules || {}) as Record<
                                  string,
                                  unknown
                                >;
                                const columnTypes = {
                                  ...((currentRules.column_types as Record<string, string>) || {}),
                                };
                                const columnLookups = {
                                  ...((currentRules.column_lookups as Record<string, string>) ||
                                    {}),
                                };

                                columnTypes[selected.field_key] = selected.field_type;
                                if (selected.lookup_category) {
                                  columnLookups[selected.field_key] = selected.lookup_category;
                                }

                                setForm((p) => ({
                                  ...p,
                                  options: [
                                    ...p.options,
                                    { value: selected.field_key, label: selected.label },
                                  ],
                                  validation_rules: {
                                    ...currentRules,
                                    column_types: columnTypes,
                                    column_lookups: columnLookups,
                                  },
                                }));
                              }
                              e.target.value = '';
                            }}
                            defaultValue=""
                          >
                            <option value="" disabled>
                              Select a field to add as column...
                            </option>
                            {fields
                              .filter(
                                (f) =>
                                  f.id !== editingField?.id &&
                                  f.field_type !== 'table' &&
                                  f.is_active,
                              )
                              .filter((f) => !form.options.some((o) => o.value === f.field_key))
                              .map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.label} ({f.field_key}) — {f.field_type}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {/* Divider */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-600" />
                        <span className="text-[10px] text-gray-400 uppercase">or</span>
                        <div className="flex-1 border-t border-gray-200 dark:border-gray-600" />
                      </div>

                      {/* Manual */}
                      <div>
                        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                          Add Manually
                        </label>
                        <div className="flex items-end gap-2">
                          <Input
                            label="Column Key"
                            value={newOptValue}
                            onChange={(e) => setNewOptValue(e.target.value)}
                            placeholder="e.g., amount"
                          />
                          <Input
                            label="Column Header"
                            value={newOptLabel}
                            onChange={(e) => setNewOptLabel(e.target.value)}
                            placeholder="e.g., Amount"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={addOption}
                            disabled={!newOptValue.trim()}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* NON-TABLE OPTIONS — keep original simple layout */}
                {form.field_type !== 'table' && (
                  <>
                    {form.options.length > 0 && (
                      <div className="mb-2 space-y-1">
                        {form.options.map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <span className="flex-1 rounded border border-border px-2 py-1 dark:border-border-dark">
                              {opt.label} <span className="text-gray-400">({opt.value})</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeOption(idx)}
                              className="text-red-500 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <Input
                        label="Value"
                        value={newOptValue}
                        onChange={(e) => setNewOptValue(e.target.value)}
                        placeholder="option_value"
                      />
                      <Input
                        label="Label"
                        value={newOptLabel}
                        onChange={(e) => setNewOptLabel(e.target.value)}
                        placeholder="Display Label"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addOption}
                        disabled={!newOptValue.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </FieldFormSection>
        )}

        {/* ── Advanced (system fields) ───────────────────────────────── */}
        {form.is_system_field && (
          <FieldFormSection
            title="Advanced"
            description="System field mapping. Changing these affects how data is stored."
          >
            <Input
              label="DB Column"
              value={form.db_column}
              onChange={(e) => setForm((p) => ({ ...p, db_column: e.target.value }))}
              placeholder="e.g., first_name"
              hint="Database column this field maps to"
              disabled={isEditing}
            />
          </FieldFormSection>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border dark:border-border-dark">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={createMutation.isPending || updateMutation.isPending}
            disabled={!form.field_key.trim() || !form.label.trim()}
          >
            {isEditing ? 'Save Changes' : 'Create Field'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
